import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Dialog } from "~/components/dialog";
import { useMultiSelectButton } from "~/hooks/use-multi-select-button";
import { useCreateFinanceReport } from "~/queries/useCreateFinanceReport";
import { useGetCounters } from "~/queries/useGetCounters";
import { COUNTER_TYPE, FINANCE_REPORT_TYPE } from "~/types/__generated/gql/graphql";
interface FinanceReportInput {
  issueDate?: Date;
  dueDate?: Date;
}
export const FinanceReportModal: FC<{
  type: FINANCE_REPORT_TYPE;
}> = ({
  type
}) => {
  const {
    data: counters = []
  } = useGetCounters();
  const [selCounter, setSelCounter] = useState<string>("");
  const [financeReport, setFinanceReport] = useState<FinanceReportInput>();
  const createFinanceReport = useCreateFinanceReport();
  const {
    selectedFiles,
    handleMultiSelectButtonClose
  } = useMultiSelectButton();
  const handleConfirm = async (type: FINANCE_REPORT_TYPE, counterId: string, issueDate?: Date, dueDate?: Date) => {
    if (!selectedFiles) return;
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        const input = {
          type: type,
          counterId: counterId,
          files: selectedFiles ?? [],
          ...(issueDate ? {
            issueDate: issueDate.toISOString()
          } : {}),
          ...(dueDate ? {
            dueDate: dueDate.toISOString()
          } : {})
        };
        await createFinanceReport.mutateAsync({
          input
        });
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Creating finance report...",
      success: "Finance report created successfully",
      error: "Failed to create finance report"
    });
    handleMultiSelectButtonClose();
  };
  useEffect(() => {
    if (counters) {
      const monthlyStatement = counters.find(c => (c.type === COUNTER_TYPE.MONTHLY_STATEMENT && c.isDefault) === true)?._id ?? null;
      if (monthlyStatement) setSelCounter(monthlyStatement);
    }
  }, [counters]);
  return <Dialog dialogProps={{
    maxWidth: "sm"
  }} title="Create Financial Report" content={<>
					<Box sx={{
      display: "flex",
      mb: 2
    }}>
						<Alert severity="info" sx={{
        width: "100%"
      }}>
							You are about to create a {`"${type}"`} finanial report.
						</Alert>
					</Box>
					<Box sx={{
      display: "flex",
      mb: 2
    }}>
						<Typography>Select a monthly statement number</Typography>
					</Box>
					<Box sx={{
      display: "flex",
      alignItems: "center",
      gap: 2
    }}>
						<TextField select label="Monthly Statement Prefix" sx={{
        width: "100%"
      }} fullWidth value={selCounter} onChange={e => setSelCounter(e.target.value)}>
							{counters.filter(i => i.type === COUNTER_TYPE.MONTHLY_STATEMENT).map(counter => <MenuItem key={counter._id} value={counter._id}>
										{counter.prefix}
									</MenuItem>)}
						</TextField>
					</Box>
					<Box sx={{
      display: "flex",
      my: 2
    }}>
						<DateTimePicker sx={{
        width: "50%",
        mr: 2
      }} label="Issue Date" value={financeReport?.issueDate ?? null} onChange={value => {
        if (!value) return;
        setFinanceReport({
          ...financeReport,
          issueDate: value
        });
      }} />
						<DateTimePicker sx={{
        width: "50%"
      }} label="Due Date" value={financeReport?.dueDate ?? null} onChange={value => {
        if (!value) return;
        setFinanceReport({
          ...financeReport,
          dueDate: value
        });
      }} />
					</Box>
					<Box sx={{
      display: "flex",
      my: 2
    }}>
						<Button variant="contained" fullWidth onClick={() => {
        if (!selCounter || selCounter === "") return;
        void handleConfirm(type, selCounter, financeReport?.issueDate, financeReport?.dueDate);
      }}>
							Create
						</Button>
					</Box>
					<Box sx={{
      display: "flex",
      mb: 2
    }}>
						<Button variant="outlined" fullWidth onClick={handleMultiSelectButtonClose}>
							Cancel
						</Button>
					</Box>
				</>} data-sentry-element="Dialog" data-sentry-component="FinanceReportModal" data-sentry-source-file="finance-report-modal.tsx" />;
};